import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Button, CircularProgress, Stack, Typography, Box, Tooltip, Avatar, Checkbox } from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { useNavigate } from 'react-router-dom';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { AppContext } from 'views/App/context';
import { DTB, DTBPackageReference } from 'views/App/Portal/DTB/_types/dtb';
import useDTBs from '../_data_sources/useDTBs';
import DTBFilters from '../_components/DTBFilters';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import datetimeHelper from 'helpers/datetimeHelper';
import { getDTBLock, takeDTBLock } from '../_data_sources/manageDTB';
import { useConfirm } from 'material-ui-confirm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { info } from '_lex/_foundations/phxColors';
import dayjs from 'dayjs';
import DtbLevelFilter from '../../_components/DtbLevelFilter';
import JurisdictionFilter from '../../_components/JurisdictionFilter';
import { PaginatedApiCollectionResponse } from 'types/api_response';
import LockIcon from '@mui/icons-material/Lock';
import { colors, useTheme } from '@mui/material';
import '../../utils/createDTBs';
import '../../utils/cleanDTBs';
import '../../utils/updateDTBs';
import { getDifferenceFlagProps } from '../../utils/flagUtils';
import { DTBLibraryContext } from '../context';
import DTBSelectionChips from '../../_components/DTBSelectionChips';
import { enqueueSnackbar } from 'notistack';
import useManageDTBPackages from '../../Packages/_data_source/useManageDTBPackage';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useQueryClient } from 'react-query';
import DTBPreview from '../Preview';
import useExportDTB from '../_data_sources/useExportDTB';
import { downloadFileFromBase64, downloadFileFromUrl } from 'views/App/Portal/Policy/DraftManuals/helpers/exportHelper';
import errorHelper from 'helpers/errorHelper';

interface LockInfo {
  displayName: string;
  avatar: string;
}

const getLockInfo = (lock: any): LockInfo => {
  if (!lock) {
    return { displayName: '', avatar: '' };
  }

  const displayName = lock.firstName ? `${lock.firstName} ${lock.lastName}` : lock.username;

  const avatar = lock.firstName
    ? [lock.firstName, lock.lastName]
      .filter((x) => x !== undefined)
      .map((x) => x![0].toLocaleUpperCase())
      .join('')
    : lock.username[0].toLocaleUpperCase();

  return { displayName, avatar };
};

export const getIconAndTooltipForTypeCol = (tFn: typeof t, type?: DTB['type']) => {
  switch (type) {
    case 'CONTENT':
      return { icon: NotesIcon, tooltip: tFn('dtbs.library.table.type.content.tooltip') };
    case 'VIDEO':
      return { icon: PlayCircleFilledWhiteOutlinedIcon, tooltip: tFn('dtbs.library.table.type.video.tooltip') };
    default:
      return { icon: NotesIcon, tooltip: tFn('dtbs.library.table.type.content.tooltip') };
  }
};

export type DTBListParams = {
  search?: string | null;
  status?: string | null;
  limit?: string | null;
  level?: string | null;
  jurisdiction?: string | null;
  manualId?: string | null;
  createdDateStart?: string | null;
  createdDateEnd?: string | null;
  distributedDateStart?: string | null;
  distributedDateEnd?: string | null;
};

const defaultParams: DTBListParams = {
  search: null,
  status: null,
  limit: null,
  level: null,
  jurisdiction: null,
  manualId: null,
  createdDateStart: null,
  createdDateEnd: null,
  distributedDateStart: null,
  distributedDateEnd: null,
};

export interface DTBListProps {
  selectionMode?: boolean;
  selectedDtbs?: DTB[];
  setSelectedDtbs?: (dtbs: DTB[]) => void;
  embedded?: boolean;
  continueDisabled?: boolean;
  onContinue?: () => void;
  manualId?: string;
  excludeIds?: string[];
  preserveExternalSelection?: boolean;
  allowLockedSelection?: boolean;
}

const DTBList: React.FC<DTBListProps> = ({
  selectionMode = false,
  selectedDtbs: selectedDtbsProp,
  setSelectedDtbs: setSelectedDtbsProp,
  embedded = false,
  continueDisabled = false,
  onContinue,
  manualId,
  excludeIds = [],
  preserveExternalSelection = false,
  allowLockedSelection = false,
}) => {
  const navigate = useNavigate();
  const { currentOrganizationId, currentProfile, isLexipolStaff, hasPermission } = useContext(AppContext);
  const confirm = useConfirm();
  const [loadingLockId, setLoadingLockId] = useState<string | null>(null);
  const theme = useTheme();
  const { approveDtbs, setDtbsFlags } = useManageDTBPackages();
  const [isApproving, setIsApproving] = useState(false);
  const [isClearingFlags, setIsClearingFlags] = useState(false);
  const queryClient = useQueryClient();
  const [previewDtbId, setPreviewDtbId] = useState<string | null>(null);
  const [previewDtbName, setPreviewDtbName] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ attempt: number; maxAttempts: number } | null>(null);

  const { exportDTB } = useExportDTB();

  // Use context for selection if not provided as props
  const context = useContext(DTBLibraryContext);
  // Add local state fallback for selection
  const [localSelectedDtbs, setLocalSelectedDtbs] = useState<DTB[]>([]);
  const selectedDtbs = selectedDtbsProp ?? context.selectedDtbs ?? localSelectedDtbs;
  const setSelectedDtbs = setSelectedDtbsProp ?? context.setSelectedDtbs ?? setLocalSelectedDtbs;

  const [params, setParams] = useUrlParams(defaultParams);
  const [dtbs, setDtbs] = useState<DTB[]>([]);

  const queryParams: Record<string, any> = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    cleanParams.status ||= 'ALL';
    if (manualId) {
      cleanParams.manualId = manualId;
    }
    if (cleanParams.createdDateStart && cleanParams.createdDateEnd) {
      cleanParams['createdDate.startDate'] = dayjs(cleanParams.createdDateStart).startOf('day').toISOString();
      cleanParams['createdDate.endDate'] = dayjs(cleanParams.createdDateEnd).endOf('day').toISOString();
    }
    if (cleanParams.distributedDateStart && cleanParams.distributedDateEnd) {
      cleanParams['distributedDate.startDate'] = dayjs(cleanParams.distributedDateStart).startOf('day').toISOString();
      cleanParams['distributedDate.endDate'] = dayjs(cleanParams.distributedDateEnd).endOf('day').toISOString();
    }
    return cleanParams;
  }, [params, manualId]);

  const dtbsQuery = useDTBs(currentOrganizationId, queryParams);

  useEffect(() => {
    if (dtbsQuery.data && !dtbsQuery.isError) {
      const fetchedDTBs = dtbsQuery.data.pages.flatMap((page: { content: DTB[]; }) => page.content) || [];
      const filteredDTBs = fetchedDTBs.filter((dtb) => !excludeIds.includes(dtb.id));
      setDtbs(filteredDTBs);
    }
  }, [dtbsQuery.data, dtbsQuery.isError]);

  const updateParams = useCallback(
    (updatedParams: DTBListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val });
    },
    [updateParams]
  );

  const onLevelChange = useCallback(
    (value: (string | null)[]) => {
      updateParams({ level: value[0] || null });
    },
    [updateParams]
  );

  const onJurisdictionChange = useCallback(
    (value: (string | null)[]) => {
      updateParams({ jurisdiction: value[0] || null });
    },
    [updateParams]
  );

  const isFiltered = useMemo(() => !_.isEmpty(_.omitBy(params, _.isEmpty)), [params]);

  const handleEditClick = async (row: any) => {
    try {
      const currentUserId = currentProfile?.user?.id;
      setLoadingLockId(row.id);
      if (row.lock) {
        if (row.lock.userId !== currentUserId) {
          const { displayName } = getLockInfo(row.lock);
          await confirm({
            cancellationButtonProps: {
              variant: 'outlined',
              color: 'neutral',
            },
            confirmationButtonProps: {
              variant: 'contained',
              color: 'error',
            },
            confirmationText: t('dtbs.library.acquireLock.ok'),
            description: t('dtbs.library.acquireLock.body', '', { lockHolder: displayName }),
            title: t('dtbs.library.acquireLock.title'),
          });
          await takeDTBLock(currentOrganizationId, row.id, (lockData) => {
            navigate(`./${row.id}`);
          });
        } else {
          navigate(`./${row.id}`);
        }
      } else {
        await getDTBLock(currentOrganizationId, row.id, (lockData) => {
          navigate(`./${row.id}`);
        });
      }
    } catch (error) {
      console.error('Error handling lock:', error);
    } finally {
      setLoadingLockId(null);
    }
  };

  const handlePreviewClick = (row: any) => {
    setPreviewDtbId(row.id);
    setPreviewDtbName(row.name);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    // Delay clearing the DTB to prevent flash during close animation
    setTimeout(() => {
      setPreviewDtbId(null);
      setPreviewDtbName('');
    }, 300); // Modal close animation duration
  };

  const handleExportClick = async (row: any) => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress(null);

    let fileName = row.name;

    try {
      await exportDTB(
        currentOrganizationId,
        row.id,
        async (response) => {
              try {
            if (response.base64) {
              // Direct base64 response (used for single content exports)
              downloadFileFromBase64({
                base64Content: response.base64,
                fileName,
                fileType: response.fileType || 'PDF',
              });
            } else if (response.presignedUrl) {
              // Presigned URL response (used for full manual exports)
              await downloadFileFromUrl({
                presignedUrl: response.presignedUrl,
                fileName,
                fileType: 'PDF',
                onProgress: (attempt, maxAttempts) => {
                  setExportProgress({ attempt, maxAttempts });
                },
              });
            } else {
              throw new Error('Invalid export response format');
            }

            enqueueSnackbar(t('draftManuals.export.exportSuccess'), { variant: 'success' });
          } catch (downloadError) {
            const errorMessage = 
              downloadError instanceof Error && downloadError.message?.includes('Time out has been exceeded')
                ? t('dtbs.export.timeoutError')
                : t('dtbs.export.downloadError');
            enqueueSnackbar(errorMessage, { variant: 'error' });
          }
        },
        (error) => {
          console.error('Export error:', error);
          errorHelper.enqueueErrors(error);
        }
      );
    } catch (error) {
      // Catch any unhandled errors to prevent React error boundary from showing
      console.error('Unhandled export error:', error);
      errorHelper.enqueueErrors(error as any);
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  // Selection helpers
  const selectedIds = selectedDtbs.map((dtb) => dtb.id);
  const handleSelectionChange = (ids: string[]) => {
    if (setSelectedDtbs) {
      if (preserveExternalSelection) {
        // Preserve previously selected items not in the current dtbs list
        const dtbsInCurrentList = dtbs.filter((dtb) => ids.includes(dtb.id));
        const dtbsNotInCurrentList = selectedDtbs.filter((dtb) => !dtbs.some((d) => d.id === dtb.id));
        setSelectedDtbs([...dtbsNotInCurrentList, ...dtbsInCurrentList]);
      } else {
        // Only use the selected items from the current list
        const selectedDtbsFromList = dtbs.filter((dtb) => ids.includes(dtb.id));
        setSelectedDtbs(selectedDtbsFromList);
      }
    }
  };

  const columns: ColumnDef = useMemo(() => {
    let baseColumns = {
      flag: {
        header: '',
        minWidth: 24,
        columnProps: {
          width: 24,
          maxWidth: 24,
        },
        renderer: (params: GridCellParams) => {
          // Show warning icon if clearanceFlag is not 'NONE'
          const warning = getDifferenceFlagProps(params.row.clearanceFlag);

          return (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}
            >
              {warning && (
                <Tooltip
                  title={warning.tooltip}
                  placement='right'
                >
                  <warning.icon sx={{ color: warning.color, fontSize: 18 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      name: {
        header: t('dtbs.library.table.title'),
        flex: 1,
        onClick: !embedded ? (params: GridCellParams) => handleEditClick(params.row) : undefined,
        renderer: (params: GridCellParams) => {
          const isLocked = !!params.row.lock;
          const { displayName, avatar } = getLockInfo(params.row.lock);
          return (
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                width: '100%',
                '& .MuiTypography-root': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                },
              }}
            >
              <Typography variant='h6'>
                {params.row.name}
                {loadingLockId === params.row.id && (
                  <CircularProgress
                    size={16}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              {isLocked && (
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{ flexShrink: 0 }}
                >
                  <Tooltip title={displayName}>
                    <Avatar
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: colors.deepPurple[500],
                        color: '#FFF',
                        fontSize: 10,
                      }}
                    >
                      {avatar}
                    </Avatar>
                  </Tooltip>
                  <LockIcon
                    color='neutral'
                    fontSize='small'
                  />
                </Stack>
              )}
            </Stack>
          );
        },
      },
      type: {
        header: t('dtbs.library.table.type.title'),
        width: 40,
        renderer: (params: GridCellParams) => {
          const { tooltip, icon: Icon } = getIconAndTooltipForTypeCol(t, params.row.type);
          return (
            <Tooltip title={tooltip}>
              <Icon color='neutral' />
            </Tooltip>
          );
        },
      },
      topic: {
        header: t('dtbs.library.table.topic'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.topic}</Typography>,
      },
      manual: {
        header: t('dtbs.library.table.manualName'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const { manual } = params.row;
          const manualTitle = manual?.manualTitle;
          const manualName = manual?.name;
          const manualId = manual?.id;
          return <Typography>{manualTitle || manualName || manualId || ''}</Typography>;
        },
      },
      package: {
        header: t('dtbs.library.table.package', 'Package'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const dtbPackages: DTBPackageReference[] = params.row.dtbPackages || [];

          if (!dtbPackages.length) {
            return <Typography>{'-'}</Typography>;
          }

          const firstPackageName = dtbPackages[0].name;
          const extraCount = dtbPackages.length - 1;

          const content = (
            <Stack
              direction='row'
              spacing={1}
              sx={{ alignItems: 'center', overflow: 'hidden' }}
            >
              <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {firstPackageName}
              </Typography>
              {extraCount > 0 && (
                <PhxBadge
                  color='neutral'
                  size='sm'
                >
                  +{extraCount}
                </PhxBadge>
              )}
            </Stack>
          );

          if (extraCount > 0) {
            return (
              <Tooltip
                title={dtbPackages.map((pkg: DTBPackageReference) => pkg.name).join(', ')}
              >
                {content}
              </Tooltip>
            );
          }

          return content;
        },
      },
      ...(isLexipolStaff
        ? {
          jurisdiction: {
            header: t('dtbs.library.table.jurisdiction', 'Jurisdiction'),
            flex: 1,
            renderer: (params: GridCellParams) => <Typography>{params.row.jurisdiction?.name}</Typography>,
          },
        }
        : {}),
      status: {
        header: t('dtbs.library.table.status'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const status = params.row.status?.toUpperCase() || 'DRAFT';
          const color = status === 'APPROVED' ? 'success' : 'neutral';
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {t(`dtbs.status.${status}`, status)}
            </PhxBadge>
          );
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 'auto',
        renderer: (params: GridCellParams) => (
          <Stack
            direction='row'
            spacing={3}
            sx={{ alignItems: 'center', display: 'flex' }}
          >
            <Tooltip
              placement='left'
              arrow
              title={
                <Stack spacing={1}>
                  <Typography variant='body2'>
                    {t('dtbs.library.table.createdAt')}: {datetimeHelper.formatDate(params.row.createdAt)}
                  </Typography>
                  <Typography variant='body2'>
                    {t('dtbs.library.table.distributedAt')}:{' '}
                    {params.row.distributedAt ? datetimeHelper.formatDate(params.row.distributedAt) : '-'}
                  </Typography>
                  <Typography variant='body2'>
                    {t('dtbs.library.table.contentReferences')}:{' '}
                    {params.row.contentReferences ? params.row.contentReferences.length : 0}
                  </Typography>
                </Stack>
              }
            >
              <InfoOutlinedIcon sx={{ color: info[800], cursor: 'pointer' }} />
            </Tooltip>
            {!embedded && (
              <ActionMenu
                id={`action_menu_${params.row.id}`}
                items={[
                  {
                    key: `preview_${params.row.id}`,
                    label: t('dtbs.library.detail.preview'),
                    onClick: () => handlePreviewClick(params.row),
                  },
                  {
                    key: `edit_${params.row.id}`,
                    label: t('common.edit'),
                    onClick: () => handleEditClick(params.row),
                  },
                  {
                    key: `export_${params.row.id}`,
                    label: t('dtbs.library.detail.export'),
                    onClick: () => handleExportClick(params.row),
                    disabled: isExporting,
                  },
                ]}
              />
            )}
          </Stack>
        ),
      },
    };
    // No custom select column, only use built-in checkbox selection
    return baseColumns;
  }, [t, navigate, loadingLockId, selectedDtbs, setSelectedDtbs, embedded, isLexipolStaff]);

  const onHandleCreateDtb = () => {
    return navigate(`./new`);
  };

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: dtbsQuery.hasNextPage,
      isFetchingNextPage: dtbsQuery.isFetchingNextPage,
      fetchNextPage: dtbsQuery.fetchNextPage,
      isError: dtbsQuery.isError,
      isLoading: dtbsQuery.isLoading,
      error: dtbsQuery.error,
      data: {
        pages:
          dtbsQuery.data?.pages.map((page: PaginatedApiCollectionResponse<DTB>) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [dtbsQuery]
  );

  const gridSection = (
    <GridSection
      columns={columns}
      data={dtbs}
      query={gridViewQuery}
      gridProps={{
        autosizeOptions: {
          columns: ['contentReferences', 'createdAt', 'distributedAt'],
          includeOutliers: true,
          includeHeaders: true,
        },
        checkboxSelection: true,
        isRowSelectable: (row: any) => (allowLockedSelection ? true : !row.lock),
        getRowClassName: (params: any) => (!allowLockedSelection && params.row.lock ? 'Mui-disabled' : ''),
        rowSelectionModel: selectedIds,
        onRowSelectionModelChange: (ids: any) => {
          const allowedIds = allowLockedSelection
            ? dtbs.map((dtb) => dtb.id)
            : dtbs.filter((dtb) => !dtb.lock).map((dtb) => dtb.id);
          const filteredSelection = ids.filter((id: string) => allowedIds.includes(id));
          handleSelectionChange(filteredSelection);
        },
      }}
    />
  );

  const handleApprove = async () => {
    if (!currentOrganizationId || !selectedDtbs.length) return;
    setIsApproving(true);
    try {
      const userId = currentProfile?.user?.id || '';
      await approveDtbs(
        currentOrganizationId,
        userId,
        selectedDtbs.map((dtb) => ({ id: dtb.id })),
        () => {
          enqueueSnackbar(t('dtbs.packages.detail.approve.success', 'DTB successfully approved.'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['dtbs', currentOrganizationId, queryParams]);
        },
        () => {
          enqueueSnackbar(t('dtbs.packages.detail.approve.error', 'Failed to approve DTB. Please try again.'), {
            variant: 'error',
          });
        }
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleClearFlags = async () => {
    if (!currentOrganizationId || !selectedDtbs.length) return;
    setIsClearingFlags(true);
    try {
      await setDtbsFlags(
        currentOrganizationId,
        selectedDtbs.map((dtb) => ({ id: dtb.id, clearanceFlag: 'NONE' })),
        () => {
          enqueueSnackbar(t('dtbs.packages.detail.actions.clearFlagsSuccess', 'Flags cleared.'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['dtbs', currentOrganizationId, queryParams]);
        },
        () => {
          enqueueSnackbar(t('dtbs.packages.detail.actions.clearFlagsError', 'Failed to clear flags.'), {
            variant: 'error',
          });
        }
      );
    } finally {
      setIsClearingFlags(false);
    }
  };

  if (embedded) {
    // Render filters/search and grid inside PageToolbar, but do NOT use CommonLayout
    return (
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                '& > *': {
                  minWidth: 'fit-content',
                  flexShrink: 0,
                },
              }}
            >
              <QuickSearch
                id='keyword_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('dtbs.library.filters.search')}
              />
              {isLexipolStaff && (
                <>
                  <Box sx={{ width: '200px' }}>
                    <DtbLevelFilter
                      id='level-picker'
                      value={params.level ? [params.level] : []}
                      onChange={onLevelChange}
                      placeholder={t('dtbs.library.level.ALL')}
                      showAllOption
                      lexipolOnly
                    />
                  </Box>
                  <Box sx={{ width: '200px' }}>
                    <JurisdictionFilter
                      id='jurisdiction-picker'
                      value={params.jurisdiction ? [params.jurisdiction] : []}
                      onChange={onJurisdictionChange}
                      showAllOption
                      size='small'
                      placeholder={t('dtbs.library.filters.allJurisdictions')}
                    />
                  </Box>
                </>
              )}
              <DTBFilters
                params={params}
                updateParams={(newFilter) => updateParams(newFilter)}
              />
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={clearParams}
                  size='small'
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: '80px',
                    px: 2,
                  }}
                >
                  {t('dtbs.library.clear')}
                </Button>
              )}
            </Stack>
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', display: 'flex' }}
            >
              {onContinue && (
                <Button
                  variant='contained'
                  onClick={onContinue}
                  disabled={continueDisabled}
                >
                  {t('common.continue')}
                </Button>
              )}
            </Stack>
          }
        />
        {dtbsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <GridSection
            columns={columns}
            data={dtbs}
            query={gridViewQuery}
            gridProps={{
              checkboxSelection: true,
              isRowSelectable: (row: any) => (allowLockedSelection ? true : !row.lock),
              getRowClassName: (params: any) => (!allowLockedSelection && params.row.lock ? 'Mui-disabled' : ''),
              rowSelectionModel: selectedIds,
              onRowSelectionModelChange: (ids: any) => {
                const allowedIds = allowLockedSelection
                  ? dtbs.map((dtb) => dtb.id)
                  : dtbs.filter((dtb) => !dtb.lock).map((dtb) => dtb.id);
                const filteredSelection = ids.filter((id: string) => allowedIds.includes(id));
                handleSelectionChange(filteredSelection);
              },
            }}
          />
        )}
      </Box>
    );
  }

  // Default: render with full layout
  return (
    <>
      <CommonLayout
        headerProps={{
          title: t('dtbs.library.title'),
          subtitle: t('dtbs.library.subTitle'),
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
          <Box>
            <PageToolbar
              leftItems={
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{
                    alignItems: 'center',
                    '& > *': {
                      minWidth: 'fit-content',
                      flexShrink: 0,
                    },
                  }}
                >
                  <QuickSearch
                    id='keyword_search'
                    initialValue={params.search || ''}
                    onChange={onSearchChange}
                    placeholder={t('dtbs.library.filters.search')}
                  />
                  {isLexipolStaff && (
                    <>
                      <Box sx={{ width: '200px' }}>
                        <DtbLevelFilter
                          id='level-picker'
                          value={params.level ? [params.level] : []}
                          onChange={onLevelChange}
                          placeholder={t('dtbs.library.level.ALL')}
                          showAllOption
                          lexipolOnly
                        />
                      </Box>
                      <Box sx={{ width: '200px' }}>
                        <JurisdictionFilter
                          id='jurisdiction-picker'
                          value={params.jurisdiction ? [params.jurisdiction] : []}
                          onChange={onJurisdictionChange}
                          showAllOption
                          size='small'
                          placeholder={t('dtbs.library.filters.allJurisdictions')}
                        />
                      </Box>
                    </>
                  )}
                  <DTBFilters
                    params={params}
                    updateParams={(newFilter) => updateParams(newFilter)}
                  />
                  {isFiltered && (
                    <Button
                      color='neutral'
                      onClick={clearParams}
                      size='small'
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: '80px',
                        px: 2,
                      }}
                    >
                      {t('dtbs.library.clear')}
                    </Button>
                  )}
                </Stack>
              }
              rightItems={
                <Stack
                  direction='row'
                  spacing={2}
                  sx={{ alignItems: 'center', display: 'flex' }}
                >
                  {(isApproving || isClearingFlags) && (
                    <CircularProgress
                      size={24}
                      sx={{ ml: 1 }}
                    />
                  )}
                  <ActionMenu
                    id='dtb-library-actions-menu'
                    btnText={'Actions'}
                    btnProps={{
                      variant: 'outlined',
                      endIcon: <MoreVertIcon />,
                      sx: { height: 40 },
                      disabled: isApproving || isClearingFlags || selectedDtbs.length === 0,
                    }}
                    items={[
                      {
                        key: 'approve',
                        label: t('dtbs.packages.detail.actions.bulkApprove', 'Approve'),
                        onClick: handleApprove,
                        disabled: isApproving || isClearingFlags || selectedDtbs.length === 0,
                      },
                      {
                        key: 'clearFlags',
                        label: t('dtbs.packages.detail.actions.clearFlags', 'Clear Flags'),
                        onClick: handleClearFlags,
                        disabled: isApproving || isClearingFlags || selectedDtbs.length === 0,
                      },
                    ]}
                  />
                  {hasPermission('createDtb', currentOrganizationId) && (
                    <Button
                      size='small'
                      onClick={onHandleCreateDtb}
                    >
                      {t('dtbs.library.createDtb')}
                    </Button>
                  )}
                </Stack>
              }
            />
            <DTBSelectionChips
              dtbs={dtbs}
              selectedDtbs={selectedDtbs.map((dtb) => dtb.id)}
              selectedCriteria={[]}
              setSelectedDtbs={(ids) => setSelectedDtbs && setSelectedDtbs(dtbs.filter((dtb) => ids.includes(dtb.id)))}
              setSelectedCriteria={() => { }}
              t={t}
            />
          </Box>
          {gridSection}
        </Box>
      </CommonLayout>
      <DTBPreview
        dtbId={previewDtbId}
        dtbName={previewDtbName}
        open={previewOpen}
        onClose={handlePreviewClose}
      />
    </>
  );
};

export default DTBList;
